/**
 * FiscalPeriodActivation.jsx
 * data-fit LLC — DecisionFlow
 * Stage 2 CONFIGURE — CONF-02: Fiscal period activation
 * Branch: feature/compliance-lifecycle-foundation
 * Spec authority: ComponentSpec_CONFIGURE.md (S82) + MasterContext_S88_Close.md
 * Path: ui/src/components/tenant-admin/FiscalPeriodActivation.jsx
 *
 * S89 audit: CLEAN — no patches required.
 * M040 C2: hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';

const API_BASE = '/api/v1';

function normalizeResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.fiscal_periods)) return payload.fiscal_periods;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function getErrorMessage(error) {
  return error?.message || 'Something went wrong.';
}

export default function FiscalPeriodActivation({ tenantId, tier, onBack, onContinue }) {
  const { token } = useAuth();

  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [activatingId, setActivatingId] = useState(null);
  const [activateError, setActivateError] = useState(null);
  const [isLoadingYears, setIsLoadingYears] = useState(false);
  const [isLoadingPeriods, setIsLoadingPeriods] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [periodLoadError, setPeriodLoadError] = useState(null);

  const selectedYear = useMemo(
    () => fiscalYears.find((year) => String(year.fiscal_year_id) === String(selectedYearId)),
    [fiscalYears, selectedYearId]
  );

  const fetchYears = useCallback(async () => {
    setIsLoadingYears(true);
    setLoadError(null);
    try {
      const response = await fetch(`${API_BASE}/fiscal-years`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Failed to load fiscal years');
      }
      const payload = await response.json();
      const rows = normalizeResponse(payload);
      setFiscalYears(rows);
      if (rows.length > 0) setSelectedYearId(rows[0].fiscal_year_id);
    } catch (error) {
      setLoadError(getErrorMessage(error));
    } finally {
      setIsLoadingYears(false);
    }
  }, [token]);

  const fetchPeriods = useCallback(async (yearId) => {
    if (!yearId) return;
    setIsLoadingPeriods(true);
    setPeriodLoadError(null);
    try {
      const response = await fetch(
        `${API_BASE}/fiscal-periods?fiscal_year_id=${encodeURIComponent(yearId)}&active_only=false`,
        {
          credentials: 'include',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Failed to load fiscal periods');
      }
      const payload = await response.json();
      setPeriods(normalizeResponse(payload));
    } catch (error) {
      setPeriodLoadError(getErrorMessage(error));
    } finally {
      setIsLoadingPeriods(false);
    }
  }, [token]);

  useEffect(() => { fetchYears(); }, [fetchYears]);
  useEffect(() => { if (selectedYearId) fetchPeriods(selectedYearId); }, [fetchPeriods, selectedYearId]);

  const activatePeriod = async (period) => {
    const periodId = period.fiscal_period_id;
    setActivatingId(periodId);
    setActivateError(null);
    try {
      const response = await fetch(`${API_BASE}/fiscal-periods/${periodId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: true })
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Failed to activate fiscal period');
      }
      const payload = await response.json().catch(() => null);
      const saved = payload?.data || payload || { ...period, is_active: true };
      setPeriods((current) =>
        current.map((existing) =>
          existing.fiscal_period_id === periodId ? { ...existing, ...saved, is_active: true } : existing
        )
      );
    } catch (error) {
      setActivateError({ periodId, message: getErrorMessage(error) });
    } finally {
      setActivatingId(null);
    }
  };

  if (loadError) {
    return (
      <section className="rounded-2xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-surface))] p-8 shadow-sm">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-danger))]">Blocking error</p>
        <h2 className="mt-2 text-tok-xl font-semibold text-[rgb(var(--color-text))]">Fiscal years could not be loaded</h2>
        <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">{loadError}</p>
        <button type="button" onClick={fetchYears} className="mt-5 rounded-xl bg-[rgb(var(--color-danger))] px-5 py-2.5 text-tok-sm font-semibold text-white">
          Retry
        </button>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-sm">
      <div className="border-b border-[rgb(var(--color-border))] p-5">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">CONF-02</p>
        <h2 className="mt-1 text-tok-xl font-semibold text-[rgb(var(--color-text))]">Fiscal period activation</h2>
        <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Activate the periods that should be available for compensation setup.
          Activation writes <span className="font-mono">is_active=true</span> and does not change period status.
        </p>
      </div>

      <div className="space-y-5 p-5">
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
          <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">Fiscal year</label>
          <select
            value={selectedYearId || ''}
            disabled={isLoadingYears}
            onChange={(e) => setSelectedYearId(e.target.value)}
            className="mt-2 w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm"
          >
            {fiscalYears.map((year) => (
              <option key={year.fiscal_year_id} value={year.fiscal_year_id}>{year.fiscal_year_label}</option>
            ))}
          </select>
          {selectedYear && (
            <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
              {selectedYear.year_start_date} → {selectedYear.year_end_date}
            </p>
          )}
        </div>

        {periodLoadError && (
          <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-4 text-tok-sm text-[rgb(var(--color-danger))]">
            <p className="font-semibold">Unable to load periods</p>
            <p className="mt-1">{periodLoadError}</p>
            <button type="button" onClick={() => fetchPeriods(selectedYearId)} className="mt-3 rounded-lg bg-[rgb(var(--color-danger))] px-3 py-2 text-white">Retry</button>
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
          <table className="min-w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
            <thead className="bg-[rgb(var(--color-surface-muted))]">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Code</th>
                <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Label</th>
                <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Dates</th>
                <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Type</th>
                <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Active</th>
                <th className="px-4 py-3 text-right font-semibold text-[rgb(var(--color-text))]">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
              {isLoadingPeriods ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[rgb(var(--color-text-muted))]">Loading fiscal periods...</td></tr>
              ) : periods.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[rgb(var(--color-text-muted))]">No fiscal periods found.</td></tr>
              ) : (
                periods.map((period) => {
                  const rowError = activateError?.periodId === period.fiscal_period_id ? activateError.message : null;
                  return (
                    <tr key={period.fiscal_period_id} className={rowError ? 'bg-[rgb(var(--color-danger))]/5' : ''}>
                      <td className="px-4 py-3 text-[rgb(var(--color-text))]">{period.period_code}</td>
                      <td className="px-4 py-3 text-[rgb(var(--color-text))]">{period.period_label}</td>
                      <td className="px-4 py-3 text-[rgb(var(--color-text))]">{period.period_start_date} → {period.period_end_date}</td>
                      <td className="px-4 py-3 text-[rgb(var(--color-text))]">{period.period_type}</td>
                      <td className="px-4 py-3 text-[rgb(var(--color-text))]">{period.period_status}</td>
                      <td className="px-4 py-3 text-[rgb(var(--color-text))]">
                        <span className={`rounded-full px-2 py-1 text-tok-xs font-medium ${period.is_active ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'}`}>
                          {period.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-[rgb(var(--color-text))]">
                        <button
                          type="button"
                          disabled={period.is_active || activatingId === period.fiscal_period_id}
                          onClick={() => activatePeriod(period)}
                          className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-2 text-tok-xs font-medium text-white disabled:cursor-not-allowed disabled:bg-[rgb(var(--color-disabled))]"
                        >
                          {activatingId === period.fiscal_period_id ? 'Activating...' : period.is_active ? 'Activated' : 'Activate'}
                        </button>
                        {rowError && <p className="mt-2 text-tok-xs text-[rgb(var(--color-danger))]">{rowError}</p>}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] p-5">
        <button type="button" onClick={onBack} className="rounded-xl border border-[rgb(var(--color-border))] px-5 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]">Back</button>
        <button type="button" onClick={onContinue} className="rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))]">Continue</button>
      </div>
    </section>
  );
}
